import { parseAmount, formatAmount } from './paycentral.helper.payment.js';
import { logger } from './paycentral.helper.logging.js';

/**
 * Manages payment amount state via delegate functions.
 * Exposed as window.Asi.PayCentral.ClientManager.
 */
export class ClientManager {
    #getValueDelegate = null;
    #setValueDelegate = null;
    #isInitialized = false;

    /**
     * Initializes ClientManager with delegate functions for getting and setting the amount.
     * @param {Object} options - Configuration options.
     * @param {Function} options.getValueDelegate - Function to retrieve the current amount.
     * @param {Function} options.setValueDelegate - Function to set the amount.
     * @throws {Error} If delegates are missing or invalid.
     */
    initialize({ getAmountValue, setAmountValue } = {}) {
        if (this.#isInitialized) {
            logger.logVerboseInfo('ClientManager already initialized. Ignoring re-initialization.');
            return;
        }
        if (typeof getAmountValue !== 'function' || typeof setAmountValue !== 'function') {
            throw new Error('ClientManager initialization failed: getAmountValue and setAmountValue must be functions.');
        }
        this.#getValueDelegate = getAmountValue;
        this.#setValueDelegate = setAmountValue;
        this.#isInitialized = true;
    }

    /**
     * Retrieves the current payment amount using the registered getValueDelegate.
     * @returns {number|null} The parsed and normalized amount, or null if unavailable or invalid.
     */
    getAmount() {
        if (!this.#getValueDelegate) {
            logger.logVerboseInfo('ClientManager getAmount: No getValueDelegate defined.');
            return null;
        }
        const value = this.#getValueDelegate();
        return parseAmount(value);
    }

    /**
     * Parses and sets the payment amount using the registered setValueDelegate.
     * @param {number|string|null|undefined} value - The input value to parse and set.
     */
    setAmount(value) {
        if (!this.#setValueDelegate) {
            logger.logVerboseInfo('ClientManager getAmount: No setValueDelegate defined.');
            return;
        }
        const parsed = parseAmount(value);
        const amount = parsed != null ? parsed : 0;
        this.#setValueDelegate(amount);
    }   
}